import { colors } from '@/theme/colors';
import { Link } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import React from 'react';

const NON_URL_FILE_EXTENSIONS = new Set([
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'txt',
  'json',
  'xml',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'zip',
  'rar',
  '7z',
]);

const isStandaloneFileName = (value: string): boolean => {
  if (!value || value.includes('/')) return false;

  const normalizedValue = value.split(/[?#]/, 1)[0];
  const extension = normalizedValue.split('.').pop()?.toLowerCase();

  return extension ? NON_URL_FILE_EXTENSIONS.has(extension) : false;
};

const shouldNormalizeBareDomain = (value: string): boolean =>
  /^[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?:\/\S*)?$/i.test(value) &&
  !isStandaloneFileName(value);

function remarkLinkifyAll() {
  return (tree: any) => {
    const isSkippable = (type?: string) =>
      type === 'link' || type === 'code' || type === 'inlineCode';

    const splitTextIntoNodes = (value: string): any[] => {
      const nodes: any[] = [];
      const pattern = new RegExp(
        [
          '((?:https?:\\/\\/|www\\.)[^\\s]+)',
          '|((?:\\b(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z]{2,})(?:\\/[\\S]*)?)(?!\\w)',
        ].join(''),
        'gi',
      );

      let lastIndex = 0;
      let match: RegExpExecArray | null;
      pattern.lastIndex = 0;

      while ((match = pattern.exec(value)) !== null) {
        const matchIndex = match.index;
        if (matchIndex > lastIndex) {
          nodes.push({
            type: 'text',
            value: value.slice(lastIndex, matchIndex),
          });
        }

        const full = match[0];
        let url = '';
        if (match[1]) {
          url = full.startsWith('http') ? full : `https://${full}`;
        } else if (match[2] && shouldNormalizeBareDomain(full)) {
          url = `https://${full}`;
        }

        if (url) {
          nodes.push({
            type: 'link',
            url,
            title: null,
            children: [{ type: 'text', value: full }],
          });
        } else {
          nodes.push({
            type: 'text',
            value: full,
          });
        }

        lastIndex = pattern.lastIndex;
      }

      if (lastIndex < value.length) {
        nodes.push({
          type: 'text',
          value: value.slice(lastIndex),
        });
      }

      return nodes.length > 0 ? nodes : [{ type: 'text', value }];
    };

    const transform = (node: any, parentType?: string): any => {
      if (!node) return node;
      if (node.type === 'text' && !isSkippable(parentType || '')) {
        const newNodes = splitTextIntoNodes(node.value || '');
        return newNodes;
      }

      if (Array.isArray(node.children) && !isSkippable(node.type)) {
        const nextChildren: any[] = [];
        for (const child of node.children) {
          const result = transform(child, node.type);
          if (Array.isArray(result)) nextChildren.push(...result);
          else nextChildren.push(result);
        }
        node.children = nextChildren;
      }

      return node;
    };

    transform(tree, undefined);
  };
}

const normalizeHref = (raw?: string): string | undefined => {
  if (!raw) return raw;
  if (/^(?:https?:)?\/\//i.test(raw)) return raw;
  if (/^(mailto:|tel:)/i.test(raw)) return raw;
  if (/^www\./i.test(raw)) return `https://${raw}`;
  if (shouldNormalizeBareDomain(raw)) {
    return `https://${raw}`;
  }
  return raw;
};

export const getMarkdownProps = (
  theme: Theme,
  handleSendMessage?: (message: string) => void,
) => ({
  remarkPlugins: [remarkGfm, remarkLinkifyAll],
  rehypePlugins: [rehypeRaw],
  components: {
    p: ({ node, ...props }: any) => (
      <p
        data-testid="markdown-paragraph"
        style={{
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          marginBlockStart: 0,
          lineHeight: 1.8,
          fontSize: '16px',
          display: 'block',
        }}
        {...props}
      />
    ),
    h1: ({ node, ...props }: any) => (
      // eslint-disable-next-line jsx-a11y/heading-has-content
      <h1
        data-testid="markdown-h1"
        style={{
          marginBlockStart: 0,
          lineHeight: 1.2,
          fontSize: '28px',
          display: 'flex',
          alignItems: 'center',
        }}
        {...props}
      />
    ),
    h2: ({ node, ...props }: any) => (
      // eslint-disable-next-line jsx-a11y/heading-has-content
      <h2
        data-testid="markdown-h2"
        style={{
          marginBlockStart: 0,
          lineHeight: 1.2,
          fontSize: '24px',
          display: 'flex',
          alignItems: 'center',
        }}
        {...props}
      />
    ),
    h3: ({ node, ...props }: any) => (
      // eslint-disable-next-line jsx-a11y/heading-has-content
      <h3
        data-testid="markdown-h3"
        style={{
          marginBlockStart: 0,
          lineHeight: 1.2,
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
        }}
        {...props}
      />
    ),
    h4: ({ node, ...props }: any) => (
      // eslint-disable-next-line jsx-a11y/heading-has-content
      <h4
        data-testid="markdown-h4"
        style={{
          marginBlockStart: 0,
          lineHeight: 1.2,
          fontSize: '18px',
          display: 'flex',
          alignItems: 'center',
        }}
        {...props}
      />
    ),
    h5: ({ node, ...props }: any) => (
      // eslint-disable-next-line jsx-a11y/heading-has-content
      <h5
        data-testid="markdown-h5"
        style={{
          marginBlockStart: 0,
          lineHeight: 1.2,
          fontSize: '16px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
        }}
        {...props}
      />
    ),
    h6: ({ node, ...props }: any) => (
      // eslint-disable-next-line jsx-a11y/heading-has-content
      <h6
        data-testid="markdown-h6"
        style={{
          marginBlockStart: 0,
          lineHeight: 1.2,
          fontSize: '16px',
          fontWeight: 'bold',
          fontStyle: 'italic',
          display: 'flex',
          alignItems: 'center',
        }}
        {...props}
      />
    ),
    ul: ({ node, ...props }: any) => (
      <ul
        data-testid="markdown-ul"
        style={{
          marginBlockStart: 0,
          marginBlockEnd: 0,
          paddingLeft: '24px',
          lineHeight: 1.8,
          fontSize: '16px',
          listStyleType: 'disc',
        }}
        {...props}
      />
    ),
    ol: ({ node, ...props }: any) => (
      <ol
        data-testid="markdown-ol"
        style={{
          marginBlockStart: 0,
          lineHeight: 1.8,
          fontSize: '16px',
        }}
        {...props}
      />
    ),
    li: ({ node, ...props }: any) => (
      <li
        data-testid="markdown-li"
        style={{
          marginBlockStart: 0,
          lineHeight: 1.8,
          fontSize: '16px',
          display: 'list-item',
          marginBottom: '10px',
        }}
        {...props}
      />
    ),
    blockquote: ({ node, ...props }: any) => (
      <blockquote
        data-testid="markdown-blockquote"
        style={{
          marginBlockStart: 0,
          lineHeight: 1.8,
          fontSize: '16px',
          borderLeft: '3px solid #ccc',
          paddingLeft: '10px',
          margin: '0 0 0 5px',
        }}
        {...props}
      />
    ),
    table: ({ node, ...props }: any) => (
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          mb: '8px',
          px: '2px',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: '6px',
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[500],
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[300],
            borderRadius: '8px',
          },
        }}
      >
        <table
          data-testid="markdown-table"
          style={{
            width: 'max-content',
            minWidth: '100%',
            marginBlockStart: 0,
            borderCollapse: 'collapse',
            tableLayout: 'auto',
            fontSize: '16px',
            marginBottom: '12px',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
          }}
          {...props}
        />
      </Box>
    ),
    thead: ({ node, ...props }: any) => (
      <thead
        data-testid="markdown-thead"
        style={{
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(0,0,0,0.03)',
        }}
        {...props}
      />
    ),
    tbody: ({ node, ...props }: any) => (
      <tbody data-testid="markdown-tbody" {...props} />
    ),
    tr: ({ node, ...props }: any) => (
      <tr
        data-testid="markdown-tr"
        style={{
          borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
        }}
        {...props}
      />
    ),
    th: ({ node, ...props }: any) => {
      const { style: _ignoredStyle, ...rest } = props || {};

      return (
        <th
          data-testid="markdown-th"
          style={{
            padding: '12px 16px',
            textAlign: 'left',
            fontWeight: 'bold',
            fontSize: '16px',
            color: theme.palette.text.primary,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
            borderLeft: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
            backgroundColor:
              theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5',
          }}
          {...rest}
        />
      );
    },
    td: ({ node, children, ...props }: any) => {
      const baseStyle = {
        padding: '10px 16px',
        fontSize: '15px',
        lineHeight: '1.5',
        verticalAlign: 'middle' as const,
      };

      const cellContent = React.Children.toArray(children).join('').trim();
      const isEmptyCell = cellContent.length === 0;
      const isDashOnly = cellContent === '-';

      const { style: _ignoredStyle, ...rest } = props || {};

      const getStyle = () => ({
        ...baseStyle,
        color: theme.palette.text.primary,
        borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
        borderLeft: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
        textAlign: isDashOnly || isEmptyCell ? 'center' : 'left',
        backgroundColor:
          theme.palette.mode === 'dark' ? '#1a1a1a' : 'transparent',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      });

      return (
        <td data-testid="markdown-td" style={getStyle()} {...rest}>
          {isEmptyCell ? '-' : children}
        </td>
      );
    },
    code: ({ node, inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      return !inline ? (
        <div
          style={{ position: 'relative', width: '100%' }}
          data-testid="markdown-code-block"
        >
          <pre
            data-testid="markdown-pre"
            style={{
              backgroundColor:
                theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '14px',
              lineHeight: 1.5,
              fontFamily: 'monospace',
              marginTop: '12px',
              marginBottom: '12px',
              width: '100%',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                fontSize: '12px',
                color: theme.palette.mode === 'dark' ? '#aaa' : '#666',
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(0,0,0,0.05)',
              }}
            >
              {language || 'code'}
            </Box>
            <code
              data-testid="markdown-code"
              style={{
                fontFamily: 'monospace',
                whiteSpace: 'pre',
              }}
              {...props}
            >
              {String(children).replace(/\n$/, '')}
            </code>
          </pre>
        </div>
      ) : (
        <code
          data-testid="markdown-inline-code"
          style={{
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.05)',
            padding: '2px 4px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '85%',
          }}
          {...props}
        >
          {children}
        </code>
      );
    },
    strong: ({ node, ...props }: any) => (
      <strong
        data-testid="markdown-strong"
        style={{
          fontWeight: 'bold',
          display: 'inline',
        }}
        {...props}
      />
    ),
    a: ({ node, children, ...props }: any) => {
      const { href, ...rest } = props || {};
      return (
        <Link
          target="_blank"
          rel="noopener noreferrer nofollow"
          data-testid="markdown-link"
          sx={{
            color:
              theme.palette.mode === 'dark'
                ? colors.royal80
                : theme.palette.secondary.main,
            textDecoration: 'underline',
            transition: 'color 0.2s ease',
            '&:hover': {
              color:
                theme.palette.mode === 'dark' ? colors.blue400 : colors.royal80,
            },
          }}
          href={normalizeHref(href)}
          {...rest}
        >
          {children}
        </Link>
      );
    },
    button: ({ node, children, ...props }: any) => {
      const handleClick = () => {
        if (handleSendMessage && children) {
          handleSendMessage(String(children));
        }
      };

      return (
        <Box sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            size="medium"
            onClick={handleClick}
            data-testid="markdown-button"
            sx={{
              margin: '8px 0',
              textTransform: 'none',
              display: 'flex',
              justifyContent: 'start',
              alignItems: 'center',
              borderRadius: '4px',
              width: 'fit-content',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              ':hover': {
                boxShadow: 'none',
              },
            }}
            {...props}
          >
            {children}
          </Button>
        </Box>
      );
    },
  },
});
